import React, { useMemo } from 'react';
import { Card, CardBody, CardHeader, Row, Col } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHeart, 
  faComments, 
  faShareAlt, 
  faMousePointer, 
  faNewspaper 
} from '@fortawesome/free-solid-svg-icons';
import { FacebookMetricsChart } from './FacebookMetricsChart';
import moment from 'moment';

interface InteractionsProps {
  facebookInsights: any;
  selectedPeriod: string;
}

export const FacebookInteractions: React.FC<InteractionsProps> = ({
  facebookInsights,
  selectedPeriod
}) => {
  // Calcular métricas de interacciones
  const interactionMetrics = useMemo(() => {
    if (!facebookInsights) return null;

    const reactions = facebookInsights.page_positive_feedback_by_type?.values?.reduce((a: number, b: number) => a + b, 0) || 0;
    const comments = facebookInsights.page_post_engagements?.values?.reduce((a: number, b: number) => a + b, 0) || 0;
    const shares = facebookInsights.page_post_engagements?.values?.reduce((a: number, b: number) => a + b, 0) || 0;
    const clicks = facebookInsights.page_total_actions?.values?.reduce((a: number, b: number) => a + b, 0) || 0;
    const posts = facebookInsights.page_posts?.values?.length || 0;

    return {
      reactions,
      comments,
      shares,
      clicks,
      posts
    };
  }, [facebookInsights]);

  // Datos para el gráfico
  const chartData = useMemo(() => {
    if (!facebookInsights?.page_positive_feedback_by_type?.values) return { labels: [], datasets: [] };

    const labels = facebookInsights.page_positive_feedback_by_type.values.map((_: any, index: number) => {
      const date = moment().subtract(facebookInsights.page_positive_feedback_by_type.values.length - index - 1, 'days');
      return date.format('DD MMM');
    });

    return {
      labels,
      datasets: [
        {
          label: 'Reacciones',
          data: facebookInsights.page_positive_feedback_by_type.values,
          borderColor: '#EC4899',
          backgroundColor: 'rgba(236, 72, 153, 0.1)',
          fill: true
        },
        {
          label: 'Comentarios',
          data: facebookInsights.page_post_engagements?.values || [],
          borderColor: '#10B981',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          fill: true
        }
      ]
    };
  }, [facebookInsights]);

  if (!interactionMetrics) {
    return (
      <Card>
        <CardHeader>
          <h4>Interacciones</h4>
        </CardHeader>
        <CardBody>
          <div className="no-data">
            <FontAwesomeIcon icon={faHeart} size="2x" />
            <p>No hay datos disponibles</p>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="facebook-interactions">
      <Card>
        <CardHeader>
          <h4>Interacciones</h4>
        </CardHeader>
        <CardBody>
          {/* Métricas de resumen */}
          <Row className="mb-4">
            <Col md={6} lg={2.4}>
              <div className="interaction-metric">
                <div className="metric-icon reactions">
                  <FontAwesomeIcon icon={faHeart} />
                </div>
                <div className="metric-info">
                  <div className="metric-value">{interactionMetrics.reactions}</div>
                  <div className="metric-label">Reacciones</div>
                </div>
              </div>
            </Col>
            <Col md={6} lg={2.4}>
              <div className="interaction-metric">
                <div className="metric-icon comments">
                  <FontAwesomeIcon icon={faComments} />
                </div>
                <div className="metric-info">
                  <div className="metric-value">{interactionMetrics.comments}</div>
                  <div className="metric-label">Comentarios</div>
                </div>
              </div>
            </Col>
            <Col md={6} lg={2.4}>
              <div className="interaction-metric">
                <div className="metric-icon shares">
                  <FontAwesomeIcon icon={faShareAlt} />
                </div>
                <div className="metric-info">
                  <div className="metric-value">{interactionMetrics.shares}</div>
                  <div className="metric-label">Compartidos</div>
                </div>
              </div>
            </Col>
            <Col md={6} lg={2.4}>
              <div className="interaction-metric">
                <div className="metric-icon clicks">
                  <FontAwesomeIcon icon={faMousePointer} />
                </div>
                <div className="metric-info">
                  <div className="metric-value">{interactionMetrics.clicks}</div>
                  <div className="metric-label">Clics</div>
                </div>
              </div>
            </Col>
            <Col md={6} lg={2.4}>
              <div className="interaction-metric">
                <div className="metric-icon posts">
                  <FontAwesomeIcon icon={faNewspaper} />
                </div>
                <div className="metric-info">
                  <div className="metric-value">{interactionMetrics.posts}</div>
                  <div className="metric-label">Posts</div>
                </div>
              </div>
            </Col>
          </Row>

          {/* Gráfico */}
          <FacebookMetricsChart
            title=""
            data={chartData}
            type="line"
            height={250}
          />
        </CardBody>
      </Card>
    </div>
  );
};
